/** tm/legal/LegalMemoFiles.tsx — 「내 메모」 첨부 파일 (2026-10-01)
 *  useMemoFiles : 목록·업로드(1개씩, 진행률)·다운로드·삭제 상태 — 메모 카드 전체가 끌어다 놓기 영역이 되도록 훅으로 분리
 *  LegalMemoFiles : 메모 아래 「첨부 파일」 영역 (버튼 + 목록 + 빈 안내)
 *  MemoFileList : 읽기 전용 목록 (업체 상세 연결 항목에서 다운로드만)
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { C, T, R } from '../components/ui';
import { getMemoFiles, getMemoFileUrl, deleteMemoFile, uploadMemoFile } from './legalApi';
import { errMsg, whenLabel, MUTED, btn } from './legalTheme';
import type { LegalMemoFile } from './legalTypes';

type Toast = (m: string, k?: 'ok' | 'error') => void;

const MAX_MB = 30;
const MAX_PER_ITEM = 50;
/** 서버(services/legal/memoFiles.js)와 같은 허용 형식 */
const ALLOWED = ['pdf', 'hwp', 'hwpx', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'];
const ACCEPT = ALLOWED.map(e => `.${e}`).join(',');

const extOf = (n: string) => { const m = /\.([A-Za-z0-9]{1,8})$/.exec(n); return m ? m[1].toLowerCase() : ''; };
export function fmtSize(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)}KB`;
  return `${(b / 1024 / 1024).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)}MB`;
}

/** 확장자 배지 색 — 문서 종류별 (법령 화면 그룹 배지와 같은 연한 바탕 + 진한 글자) */
function extTone(ext: string): { fg: string; bg: string } {
  if (ext === 'pdf') return { fg: '#B91C1C', bg: '#FEE2E2' };
  if (ext === 'hwp' || ext === 'hwpx') return { fg: '#0F766E', bg: '#CCFBF1' };
  if (ext === 'doc' || ext === 'docx' || ext === 'txt') return { fg: '#1E3A8A', bg: '#E0E7FF' };
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { fg: '#047857', bg: '#D1FAE5' };
  if (ext === 'ppt' || ext === 'pptx') return { fg: '#9A3412', bg: '#FFEDD5' };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'].includes(ext)) return { fg: '#6D28D9', bg: '#EDE9FE' };
  return { fg: C.sub, bg: C.bg };
}
function ExtBadge({ name }: { name: string }) {
  const ext = extOf(name);
  const t = extTone(ext);
  const label = (ext === 'jpeg' ? 'jpg' : ext || 'file').toUpperCase().slice(0, 4);
  return <span aria-hidden style={{ flexShrink: 0, width: 38, textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.2, padding: '3px 0', borderRadius: 4, background: t.bg, color: t.fg }}>{label}</span>;
}

/** 서명된 주소로 내려받기 — 새 창 대신 a 클릭 (await 뒤 window.open 은 팝업 차단됨) */
async function download(itemId: number, f: LegalMemoFile, onToast?: Toast) {
  try {
    const d = await getMemoFileUrl(itemId, f.id);
    if (!d?.url) throw new Error('');
    const a = document.createElement('a');
    a.href = d.url; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  } catch (e) { onToast?.(errMsg(e, '내려받지 못했습니다'), 'error'); }
}

interface Uploading { key: string; name: string; size: number; progress: number }

export interface MemoFilesState {
  files: LegalMemoFile[] | null; uploads: Uploading[];
  addFiles: (list: FileList | File[]) => void; remove: (f: LegalMemoFile) => Promise<void>; open: (f: LegalMemoFile) => void;
}

export function useMemoFiles(itemId: number, initial: LegalMemoFile[] | null | undefined, onToast: Toast): MemoFilesState {
  const [files, setFiles] = useState<LegalMemoFile[] | null>(Array.isArray(initial) ? initial : null);
  const [uploads, setUploads] = useState<Uploading[]>([]);
  const cur = useRef(itemId);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const count = useRef(0);
  useEffect(() => { count.current = (files?.length || 0) + uploads.length; });

  useEffect(() => {
    cur.current = itemId;
    setUploads([]);
    if (Array.isArray(initial)) { setFiles(initial); return undefined; }
    let alive = true;
    setFiles(null);
    getMemoFiles(itemId).then(r => { if (alive) setFiles(r); }).catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
    // initial 은 항목을 새로 열 때만 반영 (같은 항목 재조회로 목록이 덮이지 않게)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const addFiles = useCallback((list: FileList | File[]) => {
    const all = Array.from(list);
    if (!all.length) return;
    const bad = all.filter(f => !ALLOWED.includes(extOf(f.name)));
    const big = all.filter(f => ALLOWED.includes(extOf(f.name)) && f.size > MAX_MB * 1024 * 1024);
    let ok = all.filter(f => !bad.includes(f) && !big.includes(f));
    if (bad.length) onToast(`첨부할 수 없는 형식입니다: ${bad.map(f => f.name).join(', ')}`, 'error');
    else if (big.length) onToast(`${MAX_MB}MB 넘는 파일은 첨부할 수 없습니다: ${big.map(f => f.name).join(', ')}`, 'error');
    const room = MAX_PER_ITEM - count.current;
    if (ok.length > room) {
      onToast(`한 항목에는 파일을 ${MAX_PER_ITEM}개까지 둘 수 있습니다`, 'error');
      ok = ok.slice(0, Math.max(0, room));
    }
    if (!ok.length) return;
    const id = itemId;
    const stamp = Date.now();
    const jobs = ok.map((f, i) => ({ f, key: `${stamp}-${i}-${f.name}` }));
    setUploads(u => [...u, ...jobs.map(j => ({ key: j.key, name: j.f.name, size: j.f.size, progress: 0 }))]);
    count.current += jobs.length;
    // 1개씩 차례로 (nginx 본문 한도 · 진행률 표시)
    queue.current = queue.current.then(async () => {
      let done = 0;
      for (const j of jobs) {
        const live = () => cur.current === id;
        const drop = () => { if (live()) setUploads(u => u.filter(x => x.key !== j.key)); };
        try {
          const row = await uploadMemoFile(id, j.f, p => { if (live()) setUploads(u => u.map(x => (x.key === j.key ? { ...x, progress: p } : x))); });
          if (live()) setFiles(fs => [row, ...(fs || [])]);
          done += 1;
        } catch (e) { if (live()) onToast(errMsg(e, `${j.f.name} 을(를) 올리지 못했습니다`), 'error'); }
        drop();
      }
      if (done && cur.current === id) onToast(done === 1 ? '파일을 첨부했습니다' : `파일 ${done}개를 첨부했습니다`);
    });
  }, [itemId, onToast]);

  const remove = useCallback(async (f: LegalMemoFile) => {
    const prev = files;
    setFiles(fs => (fs || []).filter(x => x.id !== f.id));
    try { await deleteMemoFile(itemId, f.id); onToast('파일을 삭제했습니다'); }
    catch (e) { if (cur.current === itemId) setFiles(prev); onToast(errMsg(e, '파일을 삭제하지 못했습니다'), 'error'); }
  }, [files, itemId, onToast]);

  const open = useCallback((f: LegalMemoFile) => { download(itemId, f, onToast); }, [itemId, onToast]);
  return { files, uploads, addFiles, remove, open };
}

// ── 화면 ──
const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', minWidth: 0 };
const nameBtn: CSSProperties = {
  border: 0, background: 'transparent', padding: 0, margin: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  fontSize: T.sm, color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%',
};
const metaStyle: CSSProperties = { fontSize: T.xs, color: MUTED, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function FileRow({ f, onOpen, onRemove }: { f: LegalMemoFile; onOpen: () => void; onRemove?: () => void }) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!confirm) return undefined;
    const t = setTimeout(() => setConfirm(false), 4000);
    return () => clearTimeout(t);
  }, [confirm]);
  return (
    <div className="lw-row" style={rowStyle}>
      <ExtBadge name={f.file_name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <button type="button" className="lw-fname" onClick={onOpen} title={`${f.file_name} 내려받기`} style={nameBtn}>{f.file_name}</button>
        <div style={metaStyle}>{fmtSize(f.size)} · {whenLabel(f.created_at)}</div>
      </div>
      {onRemove && (confirm ? (
        <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
          <button type="button" className="lw-f" onClick={() => { setConfirm(false); onRemove(); }} style={{ ...btn('danger'), padding: '3px 8px', fontSize: T.xs }}>삭제</button>
          <button type="button" className="lw-f" onClick={() => setConfirm(false)} style={{ ...btn(), padding: '3px 8px', fontSize: T.xs }}>취소</button>
        </span>
      ) : (
        <button type="button" className="lw-f lw-x" onClick={() => setConfirm(true)} aria-label={`${f.file_name} 삭제`} title="삭제"
          style={{ flexShrink: 0, border: 0, background: 'transparent', color: MUTED, cursor: 'pointer', fontSize: T.md, lineHeight: 1, padding: '4px 6px', borderRadius: R.sm }}>✕</button>
      ))}
    </div>
  );
}

function UploadRow({ u }: { u: Uploading }) {
  const pct = Math.round(u.progress * 100);
  return (
    <div style={{ ...rowStyle, position: 'relative' }} aria-live="polite">
      <ExtBadge name={u.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...nameBtn, cursor: 'default', color: C.sub, fontWeight: 500 }}>{u.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <div style={{ flex: 1, height: 3, borderRadius: 2, background: C.bg, overflow: 'hidden' }}>
            <div style={{ width: `${Math.max(4, pct)}%`, height: '100%', background: C.accent, transition: 'width .15s' }} />
          </div>
          <span style={metaStyle}>{pct >= 100 ? '저장 중…' : `${pct}%`}</span>
        </div>
      </div>
      <span className="lw-spin" style={{ color: C.accent, flexShrink: 0, margin: '0 6px' }} aria-hidden />
    </div>
  );
}

const listBox: CSSProperties = { border: `1px solid ${C.border}`, borderRadius: R.sm, background: C.white, overflow: 'hidden' };
const divider = (i: number): CSSProperties => (i ? { borderTop: `1px solid ${C.bg}` } : {});

/** 읽기 전용 목록 — 업체 상세 (다운로드만) */
export function MemoFileList({ itemId, files, onToast }: { itemId: number; files: LegalMemoFile[]; onToast?: Toast }) {
  if (!files.length) return null;
  return (
    <div style={{ ...listBox, marginTop: 6 }}>
      {files.map((f, i) => <div key={f.id} style={divider(i)}><FileRow f={f} onOpen={() => download(itemId, f, onToast)} /></div>)}
    </div>
  );
}

/** 메모 아래 「첨부 파일」 — 끌어다 놓기는 메모 카드(LegalMemoLinks)가 받는다 */
export default function LegalMemoFiles({ st }: { st: MemoFilesState }) {
  const input = useRef<HTMLInputElement>(null);
  const { files, uploads } = st;
  const n = (files?.length || 0);
  const pick = () => input.current?.click();
  return (
    <div style={{ marginTop: 10 }}>
      <style>{`.lw-fname:hover{color:${C.accent}!important;text-decoration:underline}.lw-fname:focus-visible{outline:2px solid ${C.accent};outline-offset:1px}.lw-x:hover{color:${C.red}!important;background:${C.redLt}!important}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: T.sm, fontWeight: 600, color: C.sub }}>
          첨부 파일{n > 0 && <span style={{ color: MUTED, fontWeight: 500, marginLeft: 4, fontVariantNumeric: 'tabular-nums' }}>{n}</span>}
        </span>
        <button type="button" className="lw-f" onClick={pick} style={{ ...btn(), padding: '4px 10px' }}>📎 파일 첨부</button>
        <input ref={input} type="file" multiple accept={ACCEPT} hidden
          onChange={e => { if (e.target.files) st.addFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {files === null && <div className="lw-skel" style={{ height: 40, borderRadius: R.sm }} />}
      {files !== null && n === 0 && uploads.length === 0 && (
        <button type="button" className="lw-f" onClick={pick}
          style={{ width: '100%', border: `1px dashed ${C.borderDk}`, borderRadius: R.sm, background: C.bgAlt, color: MUTED, fontSize: T.sm, fontFamily: 'inherit', padding: '10px 12px', cursor: 'pointer', textAlign: 'center', lineHeight: 1.5 }}>
          파일을 끌어다 놓거나 첨부하세요
          <span style={{ display: 'block', fontSize: T.xs, color: MUTED }}>PDF·한글·오피스·이미지·ZIP · 파일당 {MAX_MB}MB</span>
        </button>
      )}
      {files !== null && (n > 0 || uploads.length > 0) && (
        <div style={listBox}>
          {uploads.map((u, i) => <div key={u.key} style={divider(i)}><UploadRow u={u} /></div>)}
          {(files || []).map((f, i) => (
            <div key={f.id} style={divider(uploads.length + i)}>
              <FileRow f={f} onOpen={() => st.open(f)} onRemove={() => { st.remove(f); }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
